import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { ScrollText, ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { describeApiError } from '../../utils/apiError';
import {
  auditActionLabel,
  auditActorLabel,
  auditDetailSummary,
} from '../shared/audit/auditLabels';
import styles from './PlatformAdmin.module.scss';

const PAGE_SIZE = 50;

// Platform defaults — module-level so their identity is stable across renders
// (they sit in effect/callback deps).
const platformFetchLogs = (params) => platformAdminService.listAuditLogs(params);
const platformCompanyOptions = async () => {
  const data = await platformAdminService.listCompanies(false, { limit: 200 });
  return data?.companies || (Array.isArray(data) ? data : []);
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const renderDetail = (detail) => {
  if (detail == null || detail === '') return <span className={styles.muted}>—</span>;
  let obj = detail;
  if (typeof detail === 'string') {
    try {
      obj = JSON.parse(detail);
    } catch {
      return <span className={styles.detailKv}>{detail}</span>;
    }
  }
  if (typeof obj !== 'object') return <span className={styles.detailKv}>{String(obj)}</span>;
  return (
    <span className={styles.detailKv}>
      {Object.entries(obj).map(([k, v]) => (
        <span key={k}>
          {k}: <code>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</code>
        </span>
      ))}
    </span>
  );
};

/**
 * Audit log panel (spec §4.5): paginated, filterable audit trail.
 *
 * Defaults to the Platform surface; the partner surfaces reuse it through props:
 *   - fetchLogs({ companyId, partnerId, action, search, limit, offset }) → { logs, total }
 *   - loadCompanyOptions() → [{ id, name }]; pass null to hide the Company filter
 *   - partners: [{ id, name }] shows a Partner filter (platform only)
 *   - fixedPartnerId: always scope to one partner (a partner's detail page)
 *   - companyLabelFn(id, name): maps a partner home company to "direct users"
 * Callers must pass stable function identities (module-level or memoized).
 */
const AuditPanel = ({
  notify,
  fetchLogs = platformFetchLogs,
  loadCompanyOptions = platformCompanyOptions,
  partners = null,
  fixedPartnerId = null,
  companyLabelFn = null,
}) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [appliedAction, setAppliedAction] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);

  const partnerId = fixedPartnerId ?? (partnerFilter || null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchLogs({
        companyId: companyFilter || null,
        partnerId,
        action: appliedAction || null,
        search: debouncedSearch || null,
        limit: PAGE_SIZE,
        offset,
      });
      setLogs(data?.logs || []);
      setTotal(data?.total ?? (data?.logs || []).length);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load audit logs.'));
    } finally {
      setLoading(false);
    }
  }, [fetchLogs, companyFilter, partnerId, appliedAction, debouncedSearch, offset]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loadCompanyOptions) {
      setCompanies([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await loadCompanyOptions();
        if (!cancelled) setCompanies(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) notify(describeApiError(err, 'Failed to load companies.'), 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCompanyOptions, notify]);

  const companyName = useCallback(
    (id) => {
      if (id == null) return '—';
      const name = companies.find((c) => c.id === id)?.name || null;
      if (companyLabelFn) return companyLabelFn(id, name);
      return name || `#${id}`;
    },
    [companies, companyLabelFn]
  );

  const partnerName = useCallback(
    (id) => (partners || []).find((p) => Number(p.id) === Number(id))?.name || `Partner #${id}`,
    [partners]
  );

  const applyAction = () => {
    setOffset(0);
    setAppliedAction(actionFilter.trim());
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const rows = useMemo(() => logs, [logs]);

  return (
    <>
      <Box className={styles.toolbar}>
        <Box className={styles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search actor / action / target…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          {partners && fixedPartnerId == null && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Partner</InputLabel>
              <Select
                label="Partner"
                value={partnerFilter}
                onChange={(e) => { setOffset(0); setPartnerFilter(e.target.value); }}
              >
                <MenuItem value="">All partners</MenuItem>
                {partners.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {loadCompanyOptions && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Company</InputLabel>
              <Select
                label="Company"
                value={companyFilter}
                onChange={(e) => { setOffset(0); setCompanyFilter(e.target.value); }}
              >
                <MenuItem value="">All companies</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{companyName(c.id)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            size="small"
            label="Action (e.g. user.create)"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyAction()}
            sx={{ minWidth: 220 }}
          />
          <Button className={styles.outlineBtn} variant="outlined" onClick={applyAction}>
            Filter
          </Button>
        </Box>
        <IconButton className={styles.subtleBtn} onClick={load} title="Refresh">
          <RefreshCw size={16} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : rows.length === 0 ? (
        <Box className={styles.emptyState}>
          <ScrollText className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No audit entries</Typography>
          <Typography variant="body2">Nothing matches the current filters.</Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          <Box className={`${styles.auditRow} ${styles.auditHead}`}>
            <span>When</span>
            <span>Actor</span>
            <span>Action</span>
            <span>Target / detail</span>
          </Box>
          {rows.map((log) => {
            const summary = auditDetailSummary(log.action, log.detail);
            return (
              <Box key={log.id} className={styles.auditRow}>
                <span className={styles.muted}>{formatWhen(log.created_at)}</span>
                <Box>
                  <div className={styles.companyName} style={{ fontSize: 13 }}>
                    {auditActorLabel(log.actor_label)}
                  </div>
                  <div className={styles.muted}>{companyName(log.company_id)}</div>
                  {partners && fixedPartnerId == null && log.partner_id != null && (
                    <div className={styles.muted}>{partnerName(log.partner_id)}</div>
                  )}
                </Box>
                <span>
                  <Tooltip title={log.action || ''} arrow>
                    <span className={styles.actionTag}>{auditActionLabel(log.action)}</span>
                  </Tooltip>
                </span>
                <Box>
                  {(log.target_type || log.target_id != null) && (
                    <div className={styles.mono} style={{ marginBottom: 4 }}>
                      {log.target_type}{log.target_id != null ? ` #${log.target_id}` : ''}
                    </div>
                  )}
                  {summary && (
                    <div className={styles.companyName} style={{ fontSize: 12, marginBottom: 4 }}>
                      {summary}
                    </div>
                  )}
                  {renderDetail(log.detail)}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box className={styles.pagination}>
        <span>Page {page} of {totalPages} · {total} total</span>
        <IconButton
          size="small"
          className={styles.subtleBtn}
          disabled={!canPrev}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          <ChevronLeft size={18} />
        </IconButton>
        <IconButton
          size="small"
          className={styles.subtleBtn}
          disabled={!canNext}
          onClick={() => setOffset(offset + PAGE_SIZE)}
        >
          <ChevronRight size={18} />
        </IconButton>
      </Box>
    </>
  );
};

export default AuditPanel;
